import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import type { Props as InputProps } from '@/components/common/Input';
import useTranslation from 'next-translate/useTranslation';
import { EnvelopeOutline, LockFilled } from '@weee-fe/kunlunicon';
import PopoverError from '../PopoverError';
import ThirdLoginV3 from '../ThirdLoginV3';
import Trans from 'next-translate/Trans';
import GoogleRecaptchaV2 from '@/components/common/GoogleRecaptchaV2';
import { PHONE_VALID_REG } from '@/constants/reg_rules';

import { useLoginForm } from './useLoginForm';
import type { LoginFieldType } from './types';

export type { LoginFieldType } from './types';

export type LoginFieldProps = Omit<InputProps, 'prefix'> & {
  type?: LoginFieldType;
  wrapperClassName?: string;
  autoComplete?: string;
  maxLength?: number;
};

export const LoginField = forwardRef<{ focus: () => void; blur: () => void } | null, LoginFieldProps>(
  function LoginField({ type = 'email', className, wrapperClassName, autoComplete, maxLength, ...rest }, ref) {
    return (
      <div
        className={twMerge(
          'flex items-center h-5 py-2 px-3 rounded-300 border-2 border-surface-100-hairline border-solid bg-surface-100-bg',
          'focus-within:border-primary-1',
          wrapperClassName
        )}>
        {type === 'email' && (
          <EnvelopeOutline className="text-surface-100-fg-minor shrink-0 mr-2 text-[15px]" aria-hidden />
        )}
        {type === 'phone' && (
          <div className="enki-body-base text-surface-100-fg-minor shrink-0 mr-2 flex items-center">
            <span>+1</span>
            <div className="w-[1px] h-9 bg-surface-100-hairline ml-3 mr-1" />
          </div>
        )}
        <Input
          ref={ref}
          id={`wid-${type}-login-input`}
          name={type === 'email' ? 'email' : 'phone'}
          type={type === 'email' ? 'email' : 'tel'}
          className={twMerge('flex-1 min-h-0 !pl-0 !pr-0 border-0', className)}
          inputClassName="enki-body-base"
          autoComplete={autoComplete ? autoComplete : type === 'email' ? 'email' : 'phone'}
          data-testid={`wid-${type}-input-input`}
          maxLength={maxLength}
          spellCheck={false}
          {...rest}
        />
      </div>
    );
  }
);

export type LoginLayerProps = {
  onSuccess?: (value?: any, arg?: any) => void;
  redirect?: string;
  onThirdLoginSuccess?: (arg?: any, res?: any) => void;
  isLoginSignupPopup?: boolean;
  wechatPopUpSuccessUrl?: string;
  className?: string;
  defaultActiveLoginMethod?: LoginFieldType;
};

export default function LoginInput(props: LoginLayerProps) {
  const {
    onSuccess,
    redirect,
    onThirdLoginSuccess,
    isLoginSignupPopup = false,
    wechatPopUpSuccessUrl,
    className,
    defaultActiveLoginMethod
  } = props;
  const { t, lang } = useTranslation('onboarding');
  const tCommon = useTranslation('common').t;

  const {
    activeLoginMethod,
    email,
    setEmail,
    phoneNum,
    setPhoneNum,
    phoneDisplayValue,
    errorRes,
    setErrorRes,
    handleNext,
    recaptchaRef,
    onRecaptchaSuccess,
    validateEmailFormat,
    validatePhoneFormat,
    switchLoginMethod,
    redirectUrl
  } = useLoginForm({ onSuccess, redirect, defaultActiveLoginMethod });
  const placeholder = activeLoginMethod === 'email' ? t('enter_email') : t('enter_phone_num');

  const handleNextClick = (e?: React.MouseEvent) => {
    if (activeLoginMethod === 'email') {
      const v = email.trim();
      if (!v) {
        setErrorRes(t('email_error_msg'));
        return;
      }
      if (!validateEmailFormat(v)) {
        setErrorRes(t('email_error_msg'));
        return;
      }
    } else {
      const phone = phoneNum.replace(/\D/g, '');
      if (!phone || phone.length !== 10) {
        setErrorRes(t('address_err_msg_phone'));
        return;
      }
      if (!PHONE_VALID_REG.test(phone)) {
        setErrorRes(t('address_err_msg_phone'));
        return;
      }
    }
    handleNext(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter' || e.keyCode === 13) {
      e.preventDefault();
      handleNextClick();
    }
  };

  return (
    <div className={twMerge('px-6', className)} data-testid="wid-login-tab">
      <div>
        <LoginField
          type={activeLoginMethod}
          placeholder={placeholder}
          wrapperClassName="mt-5 mb-2"
          value={activeLoginMethod === 'email' ? email : phoneDisplayValue}
          onChange={activeLoginMethod === 'email' ? (v: string) => setEmail(v) : (v: string) => setPhoneNum(v)}
          onKeyDown={handleKeyDown}
          maxLength={activeLoginMethod === 'phone' ? 14 : undefined}
        />
        <Button data-testid="btn-next" type="secondary" onClick={handleNextClick} className="mt-2">
          {tCommon('continue')}
        </Button>
        <GoogleRecaptchaV2 id="ftu-recaptcha-login-v2" ref={recaptchaRef} responseCallback={onRecaptchaSuccess} />
        <PopoverError show={!!errorRes} className="mt-4">
          {errorRes}
        </PopoverError>
      </div>

      <div className="flex items-center w-full max-w-[258px] h-[30px] mx-auto mt-2">
        <div className="flex-1 h-px bg-surface-100-hairline" />
        <div className="relative z-[1] shrink-0 px-2 bg-surface-100-bg enki-body-3xs text-surface-100-fg-minor">
          {tCommon('continue_with')}
        </div>
        <div className="flex-1 h-px bg-surface-100-hairline" />
      </div>

      <ThirdLoginV3
        activeLoginMethod={activeLoginMethod}
        onLoginMethodSwitch={switchLoginMethod}
        onThirdLoginSuccess={onThirdLoginSuccess}
        isLoginSignupPopup={isLoginSignupPopup}
        wechatPopUpSuccessUrl={wechatPopUpSuccessUrl}
        redirect={redirectUrl}
      />

      <div className="pt-1 text-center">
        <Trans
          i18nKey="common:signup_terms_new"
          components={{
            p: <p data-role="signup-term" className="enki-body-2xs text-surface-100-fg-minor" />,
            term: (
              <a
                className="text-surface-100-fg-minor underline"
                href={`${process.env.NEXT_PUBLIC_WEB_HOST}/${lang}/about/terms-of-service`}
              />
            ),
            policy: (
              <a
                className="text-surface-100-fg-minor underline"
                href={`${process.env.NEXT_PUBLIC_WEB_HOST}/${lang}/about/privacy-policy`}
              />
            )
          }}
        />
      </div>

      <div className="mt-2 flex items-center justify-center enki-body-2xs text-surface-100-fg-minor">
        <LockFilled className="mr-1" />
        <span>{tCommon('account_security_message')}</span>
      </div>
    </div>
  );
}
